import { useMemo } from "react";
import { setCustomFrame, TimeFrame } from "../../../../store/slices/strategySlice";
import { PRINT_TIME_SLICE } from "../../../../_app/consts";
import { Box, Checkbox, Divider, Stack, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { _roundDec } from "../../../../utils/_roundToDecimals";
import formatNumber from "../../../../utils/formatNumber";
import { showNotification } from "../../../../store/slices/notificationSlice";
import { useDispatch } from "react-redux";
import { Colors } from "../../../../_app/types";
import Header from "../../../../_modules/Header/Header";
import PageLayout from "../../../../_components/PageLayout";
import BottomButton from "../../../../_components/BottomButton";
import Spacer from "../../../../_components/Spacer";
import CustomPaper from "../../../../_components/CustomPaper";
import AdronTypography from "../../../../_components/AdronTypography";
import { TextFieldStyled } from "../../../../_components/TextFieldStyled";
import { NumericFormatCustom } from "../../../../components/ui/StyledAdron";
import IconComponent from "../../../../_components/IconComponent";

interface Props {
  onClose: (value: boolean) => void;
  timeframes: TimeFrame[];
  onChange: (value: TimeFrame[]) => void;
  totalPercent: number;
}

const AutoStrategyTimeframe = ({ onClose, timeframes, onChange, totalPercent }: Props) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  type TimeSliceKeys = keyof typeof PRINT_TIME_SLICE;

  const handleChangeChecked = (index: number) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const updatedFrames = timeframes.map((frame, idx) =>
      idx === index ? { ...frame, checked: event.target.checked } : frame
    );
    onChange(updatedFrames);
  };

  const helperText = "Invalid timeframe value";

  const handleChangeVolume = (index: number, value: number) => {
    const updatedFrames = timeframes.map((frame, idx) =>
      idx === index ? { ...frame, assignedVolume: value } : frame
    );
    onChange(updatedFrames);
  };

  const saveTimeFrames = () => {
    onClose(false);
    dispatch(setCustomFrame(true));
    dispatch(
      showNotification({
        message: `Timeframes settings saved`,
        severity: "info",
      })
    );
  };

  const timeframeSliceEqualZero = useMemo(() => {
    return timeframes.some(
      (timeframe) => timeframe.checked === true && timeframe.assignedVolume === 0
    );
  }, [timeframes]);

  return (
    <Box
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 1000,
        background:
          theme.palette.mode === "dark" ? "rgba(13, 16, 18, 1)" : "rgba(245, 245, 245, 1)",
      }}
    >
      <PageLayout
        header={<Header pageTitle={"Timeframe Settings"} needBack onChange={onClose} />}
        bottomButton={
          <BottomButton
            label={"Save"}
            onClick={saveTimeFrames}
            disabled={totalPercent !== 1 || timeframeSliceEqualZero}
          />
        }
        contentSx={{ padding: "20px 8px 0" }}
      >
        <CustomPaper color={theme.palette.grey[500]}>
          <Stack direction={"column"} spacing={3}>
            <AdronTypography
              value={
                "For your current auto strategy, we've automatically selected optimal " +
                "timeframes based on backtest results. However, feel free to adjust and" +
                " fine-tune the timeframes to match your personal preferences or trading approach."
              }
            />
            <Stack direction={"column"} spacing={1}>
              {timeframes.map((value, index) => (
                <Stack
                  key={`timeFrameField${index}`}
                  direction={"row"}
                  alignItems={"center"}
                  justifyContent={"space-between"}
                  spacing={2}
                >
                  <Stack
                    direction={"row"}
                    alignItems={"center"}
                    spacing={"0px"}
                    sx={{ width: "100px !important" }}
                  >
                    <Checkbox
                      sx={{ position: "relative", top: 1 }}
                      color={"default"}
                      checked={value.checked}
                      onChange={handleChangeChecked(index)}
                      icon={
                        <IconComponent
                          iconName={"OutCheck"}
                          hasStroke
                          hasFill
                          size={18}
                        />
                      }
                      checkedIcon={
                        <IconComponent
                          iconName={"Checked"}
                          hasStroke
                          hasFill
                          size={18}
                        />
                      }
                    />
                    <AdronTypography
                      style={{ whiteSpace: "nowrap" }}
                      value={PRINT_TIME_SLICE[value.interval as TimeSliceKeys]}
                    />
                  </Stack>
                  <TextFieldStyled
                    sx={{ maxWidth: "240px !important" }}
                    disabled={!value.checked}
                    className={`textAlignRight`}
                    helperText={value.checked && value.assignedVolume === 0 && helperText}
                    error={value.checked && value.assignedVolume === 0}
                    fullWidth={true}
                    label=""
                    variant="outlined"
                    value={value.assignedVolume * 100}
                    onChange={(e) => handleChangeVolume(index, +e.target.value / 100)}
                    size="small"
                    type={"text"}
                    InputProps={{
                      style: { textAlign: "right" },
                      inputComponent: NumericFormatCustom as any,
                      inputProps: {
                        inputMode: "decimal",
                        pattern: "[0-9]*",
                        decimalCustom: 2,
                        suffix: "%",
                        max: 100,
                      },
                    }}
                  />
                </Stack>
              ))}
            </Stack>
            <Divider />
            <Stack direction={"row"} justifyContent={"space-between"} alignItems={"center"}>
              <Stack
                direction={"row"}
                sx={{ width: "100%" }}
                justifyContent={"space-between"}
                alignItems={"center"}
              >
                <Typography sx={{ fontWeight: 700 }} variant="h3" color="textSecondary">
                  TOTAL
                </Typography>
                <AdronTypography
                  color={totalPercent > 1 ? Colors.Destructive : undefined}
                  style={{ fontWeight: 600 }}
                  value={`${formatNumber(_roundDec(totalPercent * 100, 2))}%`}
                />
              </Stack>
            </Stack>
          </Stack>
        </CustomPaper>
        <Spacer height="95px" />
      </PageLayout>
    </Box>
  );
};

export default AutoStrategyTimeframe;
